import { useRef, useEffect, useState } from 'react';
import flatpickr from 'flatpickr';
import { CONVERSION_FEE_MULTIPLIER, DBDateFormat, ZERO_AMOUNT } from 'src/lib/constants';
import dayjs from 'dayjs';
import {
  AvailableDate,
  Option,
  EnumOptionKind,
  Currency,
  Price,
  DetailedTimeslot,
  EnumOptionAccommodationSaleType,
  OrderItemAccommodationRoom,
  EnumPriceUnit,
  AccommodationRoom,
  RecommendedAccommodationRoom,
  SelectedAccommodationRoom,
  EnumAccommodationRoomOccupancyType
} from 'src/types';
import {
  generateArrayDates,
  getTotalQty,
  isStayOrFlexible,
  getDurationDay,
  canShowAverageAdvancedGroupPrice,
  getMaxQuantityFromPrices
} from 'src/helpers';
import { sortBy, every, round, min, minBy, flatten, times } from 'lodash-es';
import { calculateTotalPrice, resolvePrice, resolvedPrices, PRICE_ZERO } from 'src/lib/price_resolution';
import useCurrencyExchange, { CurrencyExchangeFunction } from 'src/hooks/useCurrencyExchange';
import currencies from 'src/data/currencies';
import useInitialData from 'src/hooks/useInitialData';
import { useLocale } from "next-intl";
import { getFlatpickrLocale } from 'src/components/DatePicker';
import { useRouter } from 'next/router';

type BookingValues = {
  quantity: number;
  localQuantity: number;
  childQuantity: number;
  childLocalQuantity: number;
  orderItemAccommodationRooms?: (OrderItemAccommodationRoom | RecommendedAccommodationRoom)[];
};

const DATE_CELL_FORMAT = 'MMMM D, YYYY';
const minDate = dayjs().startOf('day').toDate();
const maxDate = dayjs().add(1, 'year').endOf('year').toDate();
const todayStr = dayjs().format(DBDateFormat);
const numberFormatter = new Intl.NumberFormat('en-GB', {
  notation: 'compact',
  compactDisplay: 'short',
  style: 'currency',
  currency: 'SGD',
  currencyDisplay: 'symbol',
});

function highlightNextDay(option: Option, instance: flatpickr.Instance) {
  if (option.kind !== EnumOptionKind.adventure) return;
  const selectedDate = instance.selectedDates[0];
  if (!selectedDate) return;
  let i = 0;
  let strDate = dayjs(selectedDate).format(DATE_CELL_FORMAT);
  if (!instance.calendarContainer) return;
  let currentEle: HTMLElement | null = instance.calendarContainer.querySelector(
    '[aria-label="' + strDate + '"]'
  );
  if (!currentEle) return;
  const selectedDateCssClass =
    currentEle.classList.contains('flatpickr-day-secondary') ||
    currentEle.classList.contains('disabled-checkin')
      ? 'secondary-seq-selected'
      : 'seq-selected';
  while (i < getDurationDay(option)) {
    currentEle = currentEle?.nextElementSibling as HTMLElement | null;
    currentEle?.classList?.add(selectedDateCssClass);
    currentEle?.querySelector('.flatpickr-day-price')?.classList?.add('tw-hidden');
    i += 1;
  }
}

export const separateOrderItemAccommodationRooms = (orderItemAccommodationRooms: (OrderItemAccommodationRoom | RecommendedAccommodationRoom | SelectedAccommodationRoom)[], totalQuantity: number) => {
  const hasOneSelectedDoubleRoomType = orderItemAccommodationRooms.filter(o => o.qty > 0 && o.numOfPerson > 0 && o.maxNumOfPerson === 2 && o.occupancyType !== EnumAccommodationRoomOccupancyType.shared).length === 1
  const doubleRooms = orderItemAccommodationRooms.filter((o) => o.qty >= 1 && o.numOfPerson === 2 && o.maxNumOfPerson === 2)
  const doubleRoom = doubleRooms[0]
  const totalPersonsOfOtherRooms = orderItemAccommodationRooms.filter((o) => doubleRoom?.accommodationRoomId !== o.accommodationRoomId).reduce((memo, o) => {
    return memo + (o.occupancyType === EnumAccommodationRoomOccupancyType.shared ? o.numOfPerson : o.numOfPerson * o.qty)
  }, 0)
  const isEnoughPersons = doubleRoom ? (doubleRoom.qty * doubleRoom.numOfPerson + totalPersonsOfOtherRooms) >= totalQuantity : false
  const totalPersonsOfDoubleRooms = totalQuantity - totalPersonsOfOtherRooms
  if (isEnoughPersons && hasOneSelectedDoubleRoomType && doubleRooms.length === 1 && (totalPersonsOfDoubleRooms % doubleRoom.qty !== 0 || totalPersonsOfDoubleRooms === doubleRoom.qty)) {
    return flatten(
      orderItemAccommodationRooms.map((o) => {
        if (doubleRoom.accommodationRoomId === o.accommodationRoomId) {
          let numOfRoomsWith1Person = o.qty * 2 % totalPersonsOfDoubleRooms
          numOfRoomsWith1Person = numOfRoomsWith1Person === 0 ? totalPersonsOfDoubleRooms : numOfRoomsWith1Person
          const numOfRoomsWith2Persons = (totalPersonsOfDoubleRooms - numOfRoomsWith1Person) / 2
          return (numOfRoomsWith2Persons > 0 ? [
            {
              ...o,
              numOfPerson: 2,
              qty: numOfRoomsWith2Persons
            }
          ] : []).concat(times(numOfRoomsWith1Person, () => {
            return {
              ...o,
              numOfPerson: 1,
              qty: 1
            }
          }))
        } else {
          return o
        }
      })
    )
  } else {
    return orderItemAccommodationRooms
  }
}

export function getAccommodationRoomPriceByDate(
  prices: Price[],
  option: Option,
  dateStr: string | null,
  bookingFormValues: BookingValues,
  availableDates: AvailableDate[]
) {
  const dates = availableDates.filter((o) => o.date === dateStr);

  const accommodationRoomIds = (
    dates.length > 0
      ? dates
          .map((o) => {
            return o.isOpen ? o.accommodationRoomId : undefined;
          })
          .filter((v) => v)
      : (bookingFormValues.orderItemAccommodationRooms || []).map((o) => o.accommodationRoomId)
  ) as number[];

  const capacityAccommodationRooms = accommodationRoomIds.reduce((memo, accommodationRoomId) => {
    const orderItemAccommodationRoom = (bookingFormValues.orderItemAccommodationRooms || []).find(
      (o) => o.accommodationRoomId === accommodationRoomId
    );
    memo[accommodationRoomId] =
      (dates.length > 0
        ? (dates.find((o) => o.accommodationRoomId === accommodationRoomId)?.capacity || 0)
        : (orderItemAccommodationRoom && 'roomQty' in orderItemAccommodationRoom) ? (orderItemAccommodationRoom.roomQty || 0) : 0)
    return memo;
  }, {} as { [key: number]: number });

  const priceAccommodationRooms = accommodationRoomIds.reduce((memo, accommodationRoomId) => {
    memo[accommodationRoomId] = getTotalQty(bookingFormValues) > 0 ? resolvePrice(
      prices,
      option,
      accommodationRoomId,
      dateStr,
      'foreigner',
      'adult',
      null
    ) : minBy(resolvedPrices(prices, accommodationRoomId, dateStr, 'foreigner', 'adult'), (price) => {
      return Number(price.price.amount) / (price.maxQtyForPrice || 1)
    }) as Price;
    return memo;
  }, {} as { [key: number]: Price });

  const sortedAccommodationRoomIds = sortBy(accommodationRoomIds, (id) => {
    const priceAccommodationRoom = priceAccommodationRooms[id]
    if (priceAccommodationRoom) {
      const div = getTotalQty(bookingFormValues) > 0 ? 1 : (priceAccommodationRoom.maxQtyForPrice || 1)
      return Number(priceAccommodationRoom.price.amount) / div
    } else {
      return Infinity
    }
  });
  const firstOrderItemAccommodationRoom = (
    bookingFormValues.orderItemAccommodationRooms || []
  ).find(
    (o) => o.accommodationRoomId === sortedAccommodationRoomIds[0]
  ) as OrderItemAccommodationRoom;
  let totalNumOfPerson =
    getTotalQty(bookingFormValues) || (firstOrderItemAccommodationRoom ? (firstOrderItemAccommodationRoom.occupancyType === EnumAccommodationRoomOccupancyType.shared ? 1 : firstOrderItemAccommodationRoom.maxNumOfPerson) : 0);

  const orderItemAccommodationRooms = sortedAccommodationRoomIds.reduce(
    (memo, accommodationRoomId) => {
      if (totalNumOfPerson <= 0) return memo;

      const orderItemAccommodationRoom = (bookingFormValues.orderItemAccommodationRooms || []).find(
        (o) => o.accommodationRoomId === accommodationRoomId
      );
      if (!orderItemAccommodationRoom) return memo;

      let currentRoomCap = capacityAccommodationRooms[accommodationRoomId];
      if (currentRoomCap <= 0) return memo;
      let numPersons: { [key: number]: number } = {};

      while (true) {
        // we don't need to allocate exact number of person per room
        // since it doesn't matter if a room for 2 people only accommodate one person.
        // beside that it should be consistent with what we allow users to do with accommodation selection

        if (orderItemAccommodationRoom.occupancyType === EnumAccommodationRoomOccupancyType.shared) {
          const num = min([currentRoomCap, totalNumOfPerson]) as number
          numPersons[num] = 1
          totalNumOfPerson -= num
          currentRoomCap -= num
          if (totalNumOfPerson <= 0 || currentRoomCap <= 0) {
            break;
          }
        } else {
          const num = orderItemAccommodationRoom.maxNumOfPerson
          numPersons[num] = numPersons[num] ? numPersons[num] + 1 : 1;
          totalNumOfPerson -= orderItemAccommodationRoom.maxNumOfPerson;
          currentRoomCap -= 1;
          if (totalNumOfPerson <= 0 || currentRoomCap <= 0) {
            break;
          }
        }
      }

      return memo.concat(
        Object.keys(numPersons)
          .map((v) => Number(v))
          .map((numPerson) => {
            return {
              ...orderItemAccommodationRoom,
              qty: numPersons[numPerson],
              numOfPerson: numPerson,
            };
          })
      );
    },
    [] as (OrderItemAccommodationRoom | RecommendedAccommodationRoom)[]
  );

  return separateOrderItemAccommodationRooms(orderItemAccommodationRooms, getTotalQty(bookingFormValues)).map((orderItemAccommodationRoom) => {
    const numOfPerson = min([getTotalQty(bookingFormValues), orderItemAccommodationRoom.numOfPerson]) as number
    const price = priceAccommodationRooms[orderItemAccommodationRoom.accommodationRoomId as number] || PRICE_ZERO
    return {
      price: getTotalQty(bookingFormValues) > 0 ? resolvePrice(
        prices,
        option,
        orderItemAccommodationRoom.accommodationRoomId,
        dateStr,
        'foreigner',
        'adult',
        numOfPerson
      ) : price,
      occupancyType: orderItemAccommodationRoom.occupancyType,
      maxNumOfPerson: orderItemAccommodationRoom.maxNumOfPerson,
      numOfPerson: numOfPerson,
      qty: orderItemAccommodationRoom.qty,
    };
  });
}

export const getAccommodationRoomPriceMultiplier = (obj: {
  occupancyType: EnumAccommodationRoomOccupancyType;
  qty: number;
  numOfPerson: number;
}) => {
  return obj.occupancyType === EnumAccommodationRoomOccupancyType.shared ? (obj.numOfPerson || 1) : obj.qty
}

function renderExtraInfoDateCell(
  dayEle: HTMLElement,
  prices: Price[],
  option: Option,
  bookingFormValues: BookingValues,
  hidePrice?: boolean,
  currencyExchange?: CurrencyExchangeFunction,
  currentCurrency?: Currency,
  availableDates?: AvailableDate[]
) {
  var dateStr = dayEle.getAttribute('data-date') as string;
  var divPrice = document.createElement('div');

  var currentDate = dayEle.textContent;
  var dayEleInner = document.createElement('div');
  dayEleInner.className = 'flatpickr-day-inner';

  var divDate = document.createElement('div');
  divDate.className = 'flatpickr-day-date';
  divDate.textContent = currentDate;

  const orderItemAccommodationRooms = bookingFormValues.orderItemAccommodationRooms || [];
  const totalQty = getTotalQty(bookingFormValues);
  let totalPrice: string | number =
    option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom &&
    option.priceUnit === EnumPriceUnit.perRoom
      ? orderItemAccommodationRooms.reduce((total, o) => total + o.qty, 0) > 0
        ? orderItemAccommodationRooms.reduce((total, o) => {
            const roomPrice =
              o.qty > 0
                ? calculateTotalPrice(prices, option, o.accommodationRoomId, dateStr, {
                    quantity: o.numOfPerson,
                    localQuantity: 0,
                    childQuantity: 0,
                    childLocalQuantity: 0,
                  })
                : 0;
            return total + roomPrice * getAccommodationRoomPriceMultiplier(o);
          }, 0)
        : (() => {
            const accommodationRoomPrices = getAccommodationRoomPriceByDate(
              prices,
              option,
              dateStr,
              bookingFormValues,
              availableDates || []
            );
            if (accommodationRoomPrices[0]) {
              const price = accommodationRoomPrices[0].price;
              const orderItemAccommodationRoom = (bookingFormValues?.orderItemAccommodationRooms || []).find(
                (o) => o.accommodationRoomId === price.accommodationRoomId
              );
              let div = totalQty > 0 ? 1 : (orderItemAccommodationRoom?.maxNumOfPerson || 2);
              div = price.maxQtyForPrice ? min([price.maxQtyForPrice, div]) as number : div
              div = orderItemAccommodationRoom?.occupancyType === EnumAccommodationRoomOccupancyType.shared ? 1 : div
              const totalAmount = round(
                accommodationRoomPrices.reduce((memo, o) => {
                  let amount = Number(o.price.price.amount) * getAccommodationRoomPriceMultiplier(o)
                  return memo + amount
                }, 0) / div,
                2
              )
              return totalAmount
            } else {
              return calculateTotalPrice(prices, option, null, dateStr, bookingFormValues)
            }
          })()
      : totalQty === 0 && canShowAverageAdvancedGroupPrice(option)
      ? (() => {
          const maxQuantity = getMaxQuantityFromPrices(prices)
          const lastPrice = resolvePrice(
            prices,
            option,
            null,
            dateStr,
            'foreigner',
            'adult',
            maxQuantity
          );
          const secondLastPrice = resolvePrice(
            prices,
            option,
            null,
            dateStr,
            'foreigner',
            'adult',
            maxQuantity - 1
          );
          return Number(lastPrice.price.amount) - Number(secondLastPrice.price.amount)
        })()
      : calculateTotalPrice(prices, option, null, dateStr, bookingFormValues);

  let totalMoneyAmount = { amount: currentCurrency != option.baseCurrency ? totalPrice * CONVERSION_FEE_MULTIPLIER : totalPrice, currency: option.baseCurrency };
  let totalMoney =
    currencyExchange && currentCurrency
      ? currencyExchange({ amount: totalMoneyAmount, toCurrency: currentCurrency })
      : totalMoneyAmount;
  const currency = currencies.find((o) => o.code === currentCurrency);
  const displayPrice = numberFormatter
    .format(Number(totalMoney.amount))
    .replace(/SGD\s/, currency ? currency.symbol.trim() : '');
  if (displayPrice && !hidePrice) {
    divPrice.className = 'flatpickr-day-price';
    divPrice.textContent = displayPrice;
    dayEleInner.appendChild(divPrice);
  }

  dayEleInner.appendChild(divDate);
  dayEleInner.appendChild(divPrice);
  dayEle.innerHTML = '';
  dayEle.appendChild(dayEleInner);
}

export class OptionAvailabilityChecker {
  private option: Option;
  private availableDates: AvailableDate[];
  private detailedTimeslots: DetailedTimeslot[];
  private bookingFormValues: BookingValues;
  private isChangeDatePage?: boolean;

  constructor(
    option: Option,
    availableDates: AvailableDate[],
    detailedTimeslots: DetailedTimeslot[],
    bookingFormValues: BookingValues,
    isChangeDatePage?: boolean
  ) {
    this.option = option;
    this.availableDates = availableDates;
    this.detailedTimeslots = detailedTimeslots;
    this.bookingFormValues = bookingFormValues;
    this.isChangeDatePage = isChangeDatePage
  }

  getScheduledDatesInMonth(minDate: Date, maxDate: Date) {
    var dates = generateArrayDates(minDate, maxDate);
    return dates.filter((date) => {
      return this.isGuaranteed(date);
    });
  }

  isGuaranteed(formattedDate: string) {
    let currentTime = dayjs().tz(this.option.supplierTimezone);
    let datetime = dayjs(formattedDate)
      .add(this.option.kind === EnumOptionKind.shortAdventure ? 23 : 10, 'hour')
      .tz(this.option.supplierTimezone, true);
    let earliestSelectableStartDateTime = currentTime.add(this.option.minBookingHours, 'hour');

    return (
      !!this.availableDates.find((o) => o.date === formattedDate) &&
      this.isCapacityEnough(formattedDate) &&
      datetime >= earliestSelectableStartDateTime
    );
  }

  isTryLuck(formattedDate: string) {
    return formattedDate >= todayStr && !this.isGuaranteed(formattedDate);
  }

  getAvailableTimeslotByDate(dateStr: string) {
    const comparableDateTimeFormat = 'YYYY-MM-DD HH:mm';
    let currentTime = dayjs().tz(this.option.supplierTimezone);
    let earliestSelectableStartDateTime = currentTime.add(this.option.minBookingHours, 'hour');
    let earliestSelectableStartDateTimeString =
      earliestSelectableStartDateTime.format(comparableDateTimeFormat);

    return sortBy(
      this.detailedTimeslots.filter((timeslot) => {
        return (
          timeslot.date === dateStr &&
          timeslot.isOpen &&
          dayjs(timeslot.datetime).format(comparableDateTimeFormat) >=
            earliestSelectableStartDateTimeString &&
          timeslot.capacity >= this.getCapacity()
        );
      }),
      'start_time'
    );
  }

  hasNoAvailableTimeslotByDate(dateStr: string) {
    if (this.option.kind === EnumOptionKind.shortAdventure && dateStr >= todayStr) {
      return this.getAvailableTimeslotByDate(dateStr).length === 0;
    } else {
      return false;
    }
  }

  isCapacityEnough(formattedDate: string) {
    if (this.option.kind === EnumOptionKind.shortAdventure) {
      return this.getAvailableTimeslotByDate(formattedDate).length > 0;
    } else if (this.option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom) {
      if (this.isChangeDatePage) {
        const orderItemAccommodationRooms = (this.bookingFormValues.orderItemAccommodationRooms || []).filter((o) => o.qty > 0)
        const groupedOrderItemAccommodationRooms = orderItemAccommodationRooms.reduce((memo, orderItemAccommodationRoom) => {
          if (memo[orderItemAccommodationRoom.accommodationRoomId]) {
            memo[orderItemAccommodationRoom.accommodationRoomId] = {
              ...memo[orderItemAccommodationRoom.accommodationRoomId],
              qty: memo[orderItemAccommodationRoom.accommodationRoomId].qty + orderItemAccommodationRoom.qty
            }
          } else {
            memo[orderItemAccommodationRoom.accommodationRoomId] = orderItemAccommodationRoom
          }
          return memo
        }, {} as { [key: number]: (OrderItemAccommodationRoom | RecommendedAccommodationRoom)})
        return Object.keys(groupedOrderItemAccommodationRooms).every((accommodationRoomId) => {
          const orderItemAccommodationRoom = groupedOrderItemAccommodationRooms[Number(accommodationRoomId)]
          return (this.availableDates.find((o) => o.date === formattedDate && o.accommodationRoomId === orderItemAccommodationRoom.accommodationRoomId)?.capacity || 0) >= orderItemAccommodationRoom.qty
        }, 0)
      } else {
        const maxCap = this.availableDates
          .filter((o) => o.date === formattedDate)
          .reduce((total, o) => total + (o.accommodationRoomOccupancyType === EnumAccommodationRoomOccupancyType.shared ? o.capacity : o.capacity * o.maxNumOfPerson), 0);
        return this.getCapacity() <= maxCap && maxCap > 0;
      }
    } else {
      const availableDate = this.availableDates.find((o) => o.date === formattedDate);
      return availableDate ? this.getCapacity() <= availableDate.capacity : false;
    }
  }

  getCapacity() {
    return this.option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom
      ? getTotalQty(this.bookingFormValues)
      : this.option.isPrivateTrip
      ? 1
      : getTotalQty(this.bookingFormValues);
  }
}

class DateCellEnhancer {
  private option: Option;
  private availabilityChecker: OptionAvailabilityChecker;
  private instanceFlatpickr: flatpickr.Instance;
  private availableDates: AvailableDate[];

  constructor(
    option: Option,
    availabilityChecker: OptionAvailabilityChecker,
    instanceFlatpickr: flatpickr.Instance,
    availableDates: AvailableDate[]
  ) {
    this.option = option;
    this.availabilityChecker = availabilityChecker;
    this.instanceFlatpickr = instanceFlatpickr;
    this.availableDates = availableDates;
  }

  getLastCheckoutAbleDate() {
    if (!this.instanceFlatpickr.selectedDates[0]) return null;
    var startDate = new Date(this.instanceFlatpickr.selectedDates[0].getTime());
    var date = new Date(this.instanceFlatpickr.selectedDates[0].getTime());
    var i = 0;
    var numRetries = 45;
    var res;
    while (i <= numRetries) {
      if (i === 0) {
        i += 1;
        continue;
      }

      var numNights = dayjs(date).diff(dayjs(startDate), 'day');
      if (
        this.availabilityChecker.isTryLuck(dayjs(date).format(DBDateFormat)) &&
        numNights >= Number(this.option.minDurationNight || 1)
      ) {
        res = date;
        break;
      } else {
        date.setDate(date.getDate() + 1);
        i += 1;
      }
    }
    return res;
  }

  isGuaranteedCheckout(useSelectedCheckoutDate?: boolean) {
    var lastCheckoutAbleDate = useSelectedCheckoutDate
      ? this.instanceFlatpickr.selectedDates[1] || this.getLastCheckoutAbleDate()
      : this.getLastCheckoutAbleDate();

    var startDate = new Date(this.instanceFlatpickr.selectedDates[0].getTime());
    if (lastCheckoutAbleDate) {
      var endDate = new Date(lastCheckoutAbleDate.getTime());
      endDate.setDate(endDate.getDate() - 1);
      return every(generateArrayDates(startDate, endDate), (date) => {
        return this.isAvailable(date);
      });
    } else {
      var i = 0;
      var res = true;
      var minDurationNight = this.option.minDurationNight || 1;
      while (i < minDurationNight) {
        if (this.isAvailable(dayjs(startDate).format(DBDateFormat))) {
          startDate.setDate(startDate.getDate() + 1);
        } else {
          res = false;
        }
        i += 1;
      }
      return res;
    }
  }

  setSelectedCheckinCellState() {
    var startDate = this.instanceFlatpickr.selectedDates[0];
    var lastCheckoutAbleDate = this.getLastCheckoutAbleDate();
    if (!this.instanceFlatpickr.daysContainer) return;

    this.instanceFlatpickr.daysContainer
      .querySelectorAll('.flatpickr-day:not(.flatpickr-disabled)')
      .forEach((dayEle) => {
        if (!(dayEle instanceof HTMLElement)) return;

        var currentEleDate = dayjs(dayEle.dataset.date).toDate();
        if (currentEleDate < startDate) {
          // disable
          dayEle.classList.add('flatpickr-disabled', 'invalid-checkout');
        } else if (
          dayjs(currentEleDate).format(DBDateFormat) === dayjs(startDate).format(DBDateFormat)
        ) {
          if (this.option.kind !== EnumOptionKind.flexibleAdventure) {
            dayEle.classList.add('notAllowed');
          }
        } else if (currentEleDate > startDate) {
          var numNights = dayjs(currentEleDate).diff(startDate, 'day');
          if (numNights >= Number(this.option.minDurationNight || 1)) {
            if (
              (lastCheckoutAbleDate && currentEleDate > lastCheckoutAbleDate) ||
              !this.isGuaranteedCheckout()
            ) {
              dayEle.classList.add('booked-checkout');
              dayEle.classList.add('notAllowed');
            } else {
              dayEle.classList.add('enabled-checkout');
              dayEle.classList.remove('notAllowed');
            }
          } else {
            dayEle.classList.add('flatpickr-disabled', 'disabled-checkout');
          }
        }
      });
    // initDisabledCheckoutTooltip();
  }

  resetCellState(selectedStartEnd?: boolean) {
    if (!this.instanceFlatpickr.daysContainer) return;

    this.instanceFlatpickr.daysContainer
      .querySelectorAll('.disabled-checkout, .invalid-checkout')
      .forEach(function (ele) {
        if (ele instanceof HTMLElement) {
          ele.classList.remove('disabled-checkout', 'invalid-checkout', 'flatpickr-disabled');
        }
      });

    this.instanceFlatpickr.daysContainer
      .querySelectorAll(
        '.enabled-checkout:not(.selected-checkout, .selected-luck-checkout), .booked-checkout:not(.selected-checkout, .selected-luck-checkout)'
      )
      .forEach((ele) => {
        if (ele instanceof HTMLElement) {
          ele.classList.remove(
            'enabled-checkout',
            'booked-checkout',
            'selected-checkout',
            'selected-luck-checkout',
            'first-luck'
          );
          if (selectedStartEnd) {
            var currentDate = dayjs(ele.getAttribute('aria-label'), DATE_CELL_FORMAT).toDate();
            var formattedDate = dayjs(currentDate).format(DBDateFormat);
            if (this.availabilityChecker.isGuaranteed(formattedDate)) {
              ele.classList.remove('notAllowed');
            }
          }
        }
      });
  }

  draw(resetState?: boolean) {
    if (!isStayOrFlexible(this.option)) return;

    if (!this.instanceFlatpickr.calendarContainer) return;
    if (this.instanceFlatpickr.selectedDates.length === 2) {
      // replicate checkin-state
      this.setSelectedCheckinCellState();
      // highlight date between start and end dates
      var startDate = this.instanceFlatpickr.selectedDates[0];
      var endDate = this.instanceFlatpickr.selectedDates[1];
      var strStartDate = dayjs(startDate).format(DATE_CELL_FORMAT);
      var strEndDate = dayjs(endDate).format(DATE_CELL_FORMAT);
      var endDateEle = this.instanceFlatpickr.calendarContainer.querySelector(
        '[aria-label="' + strEndDate + '"]'
      );

      var cssClass =
        endDateEle instanceof HTMLElement
          ? endDateEle.classList.contains('booked-checkout')
            ? ['selected-luck-checkout']
            : ['selected-checkout']
          : [];
      var firstCssClass =
        endDateEle instanceof HTMLElement
          ? endDateEle.classList.contains('booked-checkout')
            ? ['notAllowed', 'selected-luck-checkout', 'first-luck']
            : ['notAllowed', 'selected']
          : [];

      generateArrayDates(startDate, endDate)
        .map((date) => {
          return dayjs(date).toDate();
        })
        .forEach((date) => {
          var strDate = dayjs(date).format(DATE_CELL_FORMAT);
          if (this.instanceFlatpickr.calendarContainer instanceof HTMLElement) {
            const dayEle = this.instanceFlatpickr.calendarContainer.querySelector(
              '[aria-label="' + strDate + '"]'
            );
            if (dayEle instanceof HTMLElement) {
              dayEle.classList.add(
                ...(dayjs(startDate).format(DBDateFormat) === dayjs(date).format(DBDateFormat)
                  ? firstCssClass
                  : cssClass)
              );
            }
          }
        });
      // remove price from end date
      endDateEle instanceof HTMLElement &&
        endDateEle.querySelector('.flatpickr-day-price')?.classList?.add('tw-hidden');

      // add border black for one day
      if (endDateEle instanceof HTMLElement && strEndDate === strStartDate) {
        endDateEle.classList.add('inOneDay');
      }

      // return to normal/checkin-state
      this.resetCellState(true);
    } else if (this.instanceFlatpickr.selectedDates.length === 1) {
      if (resetState) {
        this.resetCellState();
      }
      this.setSelectedCheckinCellState();
    } else {
      this.resetCellState();
    }
  }

  isAvailable(formattedDate: string) {
    return this.availableDates.find((o) => o.date === formattedDate);
  }
}

type Props = {
  instanceRef: React.MutableRefObject<flatpickr.Instance | undefined>;
  datepickerProps?: Record<string, any>;
  flatpickrClassName?: string;
  availableDates: AvailableDate[];
  option: Option;
  prices: Price[];
  hidePrice?: boolean;
  currentCurrency: Currency;
  bookingFormValues: BookingValues & { startDate: string | null };
  detailedTimeslots: DetailedTimeslot[];
  onChange?: (dates: Date[], instance: flatpickr.Instance) => void;
  onClick?: (date: string, dayEle: HTMLElement, instance: flatpickr.Instance) => void;
  onReady?: () => void;
  onMonthChange?: (date: string) => void;
  onStartDateClosed?: () => void;
  viewOnly?: boolean;
};

const OptionDatepicker: React.FC<Props> = ({
  instanceRef,
  datepickerProps,
  flatpickrClassName,
  option,
  availableDates,
  currentCurrency,
  bookingFormValues,
  detailedTimeslots,
  prices,
  hidePrice,
  viewOnly,
  onChange,
  onClick,
  onMonthChange,
  onStartDateClosed
}) => {
  const [isChangeDatePage, setIsChangeDatePage] = useState(false)
  const router = useRouter()
  const [currentMonth, setCurrentMonth] = useState<string>();
  const locale = useLocale();
  const eleRef = useRef<HTMLDivElement>(null);
  const availabilityChecker = new OptionAvailabilityChecker(
    option,
    availableDates,
    detailedTimeslots,
    bookingFormValues,
    isChangeDatePage
  );
  const totalQuantity = getTotalQty(bookingFormValues);
  const currencyExchange = useCurrencyExchange();
  const { exchangeRates } = useInitialData();
  const minAvailableDate = availableDates.length > 0 ? dayjs(availableDates[0].date).startOf('day').toDate() : minDate

  useEffect(() => {
    if (eleRef.current) {
      instanceRef.current = flatpickr(eleRef.current, {
        locale: getFlatpickrLocale(locale),
        mode: 'single',
        inline: true,
        static: true,
        minDate: minAvailableDate,
        maxDate: maxDate,
        prevArrow:
          '<svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18"></path></svg>',
        nextArrow:
          '<svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>',
        dateFormat: 'd-m-Y',
        ...(datepickerProps || {}),
        enable: [
          function (date: Date) {
            var formattedDate = dayjs(date).format(DBDateFormat);
            return availabilityChecker.isGuaranteed(formattedDate);
          },
        ],
        onDayCreate: function (_, __, instance, dayEle: HTMLElement) {
          const dateObj = (dayEle as any).dateObj as Date;
          var formattedDate = dayjs(dateObj, 'MMMM D, YYYY').format(
            DBDateFormat
          );
          if (
            availabilityChecker.isTryLuck(formattedDate) ||
            availabilityChecker.hasNoAvailableTimeslotByDate(formattedDate)
          ) {
            dayEle.classList.add('flatpickr-day-secondary');
            dayEle.classList.remove('flatpickr-disabled');
            dayEle.classList.add('notAllowed');
          }
          if (viewOnly) {
            dayEle.classList.add('notAllowed');
          }
          dayEle.setAttribute('data-date', formattedDate);
          renderExtraInfoDateCell(
            dayEle,
            prices,
            option,
            bookingFormValues,
            hidePrice,
            currencyExchange,
            currentCurrency,
            availableDates
          );
        },
        onReady: function (_, __, instance) {
          const selectMonthEle: HTMLSelectElement | null = instance.calendarContainer.querySelector(
            '.flatpickr-monthDropdown-months'
          );
          if (selectMonthEle) {
            selectMonthEle.disabled = true;
          }

          const numInputEle: HTMLInputElement | null =
            instance.calendarContainer.querySelector('.numInput');
          if (numInputEle) {
            numInputEle.disabled = true;
          }

          const arrowUpEle: HTMLElement | null =
            instance.calendarContainer.querySelector('.arrowUp');
          if (arrowUpEle) arrowUpEle.remove();
          const arrowDownEle: HTMLElement | null =
            instance.calendarContainer.querySelector('.arrowDown');
          if (arrowDownEle) arrowDownEle.remove();

          setCurrentMonth(`${instance.currentYear}-${instance.currentMonth + 1}-01`);
          new DateCellEnhancer(
            option,
            availabilityChecker,
            instance,
            availableDates
          ).draw();
          highlightNextDay(option, instance);

          instance.calendarContainer.addEventListener('mouseup', (e: MouseEvent) => {
            if (e.target instanceof HTMLElement) {
              const dayEle: HTMLElement | null = e.target.closest('.flatpickr-day');
              if (dayEle && !dayEle.classList.contains('flatpickr-day-secondary')) {
                typeof onClick === 'function' &&
                  onClick(dayEle.dataset.date as string, dayEle, instance);
              }
            }
          });
        },
        onChange: function (dates, _, instance) {
          if (viewOnly) return;
          const resetState = dates.length > 2 ? true : false;

          if (typeof onChange === 'function') {
            onChange(dates, instance);
          }

          new DateCellEnhancer(
            option,
            availabilityChecker,
            instance,
            availableDates
          ).draw(resetState);
          highlightNextDay(option, instance);
        },
        onMonthChange: function (_, __, instance) {
          const date = `${instance.currentYear}-${instance.currentMonth + 1}-01`
          setCurrentMonth(date);
          new DateCellEnhancer(
            option,
            availabilityChecker,
            instance,
            availableDates
          ).draw();
          highlightNextDay(option, instance);

          if (typeof onMonthChange === 'function') {
            onMonthChange(date);
          }
        },
      });
    }
  }, [
    option.id,
    viewOnly,
    JSON.stringify(datepickerProps),
    availableDates.length,
    detailedTimeslots.length,
    getTotalQty(bookingFormValues),
    currentCurrency,
    exchangeRates.length,
    isChangeDatePage
  ]);

  useEffect(() => {
    // TODO: detailedTimeslots.length > 0 is not reliable. if start date is on a month very far from current month.
    // detailed timeslots of the date is only present after a few fetch detailed_timeslots requests.
    if (
      bookingFormValues.startDate && detailedTimeslots.length > 0 && (
        availabilityChecker.isTryLuck(bookingFormValues.startDate) ||
        availabilityChecker.hasNoAvailableTimeslotByDate(bookingFormValues.startDate)
      )
    ) {
      onStartDateClosed && onStartDateClosed()
    }

  }, [getTotalQty(bookingFormValues), bookingFormValues.startDate, detailedTimeslots.length])

  useEffect(() => {
    setIsChangeDatePage(router.pathname === '/account/trips/[id]/change-date')
  }, [router])

  return (
    <>
      <div className={`flatpickr-brand ${flatpickrClassName || ''}`}>
        <div ref={eleRef}></div>
      </div>
    </>
  );
};

export default OptionDatepicker;
